// Minimal toast/notification event bus and helpers
// Keeps UI-free API for services and components to trigger notifications

import i18n from '../i18n/config';
import { SystemErrorDetails } from '../types/notification';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export type ToastMessage = {
  id: string;
  title?: string;
  description?: string;
  /** Optional internal route shown as a "View" link in the notification panel */
  link?: string;
  variant?: ToastVariant;
  durationMs?: number;
  /** When true, the notification is a system/API/backend error — only visible to super-admins */
  isSystemError?: boolean;
  /** Full diagnostic details for system errors */
  systemErrorDetails?: SystemErrorDetails;
};

type Listener = (msg: ToastMessage) => void;

class ToastBus {
  private listeners = new Set<Listener>();

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(message: Omit<ToastMessage, 'id'>) {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const withDefaults: ToastMessage = {
      id,
      durationMs: 4000,
      variant: 'info',
      ...message,
    };
    for (const cb of this.listeners) cb(withDefaults);
    return id;
  }
}

const bus = new ToastBus();

// Default titles are resolved at call time (default parameter expressions) so
// they follow the language active when the notification is emitted.
export const notify = Object.freeze({
  subscribe: (listener: Listener) => bus.subscribe(listener),
  show: (opts: Omit<ToastMessage, 'id'>) => bus.emit(opts),
  success: (description: string, title: string = i18n.t('notifications.titles.success')) =>
    bus.emit({ description, title, variant: 'success' }),
  error: (description: string, title: string = i18n.t('notifications.titles.error')) =>
    bus.emit({ description, title, variant: 'error', durationMs: 6000 }),
  info: (description: string, title: string = i18n.t('notifications.titles.info')) =>
    bus.emit({ description, title, variant: 'info' }),
  warning: (description: string, title: string = i18n.t('notifications.titles.warning')) =>
    bus.emit({ description, title, variant: 'warning' }),
  /**
   * System-level error (API/backend failure, infrastructure issue).
   * Only visible to super-admins. Accepts an optional Error object or
   * SystemErrorDetails for full diagnostic capture.
   */
  systemError: (
    description: string,
    errorOrDetails?: Error | SystemErrorDetails | unknown,
    title: string = i18n.t('notifications.titles.systemError'),
  ) => {
    let details: SystemErrorDetails | undefined;

    if (errorOrDetails instanceof Error) {
      details = {
        errorType: errorOrDetails.name,
        stack: errorOrDetails.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
      };
    } else if (errorOrDetails && typeof errorOrDetails === 'object') {
      details = errorOrDetails as SystemErrorDetails;
    }

    return bus.emit({
      description,
      title,
      variant: 'error',
      durationMs: 8000,
      isSystemError: true,
      systemErrorDetails: details,
    });
  },
});

export default notify;
